import { Plus, Trash2 } from 'lucide-react';
import { AutosaveTextInput } from '@/components/Admin/Autosave/AutosaveTextInput';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  cleanOptionalServices,
  experienceMaxOptionalServices,
  makeOptionalService,
  nextOptionalServiceOrder,
  normalizeOptionalServices,
  optionalServiceLabelMaxLength,
  validateOptionalServices,
  type ExperienceOptionalServiceType,
} from '@/types/types_experience';
import { formatJpy } from '@/util/util_format';

interface OptionalServicesFieldProps {
  value: Record<string, ExperienceOptionalServiceType>;
  /** Change some fields of one row, leaving every other row untouched. */
  onPatch: (service: ExperienceOptionalServiceType, changes: Partial<ExperienceOptionalServiceType>) => void;
  onAdd: (service: ExperienceOptionalServiceType) => void;
  onRemove: (service: ExperienceOptionalServiceType) => void;
}

/**
 * The extras a partner may tick when it asks for this experience — a private
 * shuttle, an interpreter, a lunch afterwards.
 *
 * A controlled component like `AvailabilityClosuresField` next to it: the page
 * commits each change, which is what puts this list on the autosave, history and
 * undo stack with everything else on the form. Deleting a row is therefore
 * undoable, which is why it is not confirmed.
 *
 * Reports each edit as the one row it touched rather than as a new list, because
 * that is how it is stored — see `ExperienceType.optionalServices`. Repricing a
 * shuttle writes `optionalServices.{id}.price`, so a colleague editing another
 * row at the same time keeps their change.
 *
 * Editing a row in place *is* offered, unlike the closures, and it is safe for a
 * reason worth stating: a booking snapshots the services it was made with, so
 * renaming or repricing one here changes what the next partner is offered and
 * nothing about what an earlier one agreed to. The row's id is what makes that
 * true, and it is minted once and never shown.
 *
 * An empty list is the ordinary case. Most experiences offer nothing here, and
 * the public form shows no section at all when they do not.
 */
export function OptionalServicesField({ value, onPatch, onAdd, onRemove }: OptionalServicesFieldProps) {
  const services = normalizeOptionalServices(value);

  // Reported rather than prevented: the boxes stay editable and the admin is
  // told what is wrong with them, which is the same bargain the rest of this
  // form makes. Judged on the cleaned rows, so a half-typed new row is not an
  // error until it has something in it.
  const problem = validateOptionalServices(cleanOptionalServices(services));

  return (
    <div className='tw:space-y-3'>
      <p className='tw:text-xs tw:text-muted-foreground'>
        Offered as unticked boxes on the booking form, above the special requests. The price is for the
        booking rather than per guest, and it is added to what the partner is quoted — so tick
        &ldquo;Commission&rdquo; only where the partner earns its rate on the service as well as on the
        experience.
      </p>

      {services.length > 0 && (
        <ul className='tw:divide-y tw:rounded-md tw:border'>
          {services.map((service) => (
            <li key={service.id} className='tw:flex tw:flex-wrap tw:items-center tw:gap-3 tw:px-3 tw:py-2'>
              <div className='tw:min-w-48 tw:flex-1'>
                <AutosaveTextInput
                  currentValue={service.label}
                  placeholder='Private shuttle'
                  onCommitChange={(v) => onPatch(service, { label: v.slice(0, optionalServiceLabelMaxLength) })}
                />
              </div>
              <div className='tw:w-32'>
                <AutosaveTextInput
                  numeric
                  currentValue={service.price ? String(service.price) : ''}
                  placeholder='¥'
                  onCommitChange={(v) => onPatch(service, { price: Number(v) || 0 })}
                />
              </div>
              <label
                className='tw:flex tw:cursor-pointer tw:items-center tw:gap-2 tw:text-xs tw:text-muted-foreground'
                title='The partner earns its commission on this service.'
              >
                <Checkbox
                  checked={service.commissionable}
                  onCheckedChange={(checked) => onPatch(service, { commissionable: checked === true })}
                />
                Commission
              </label>
              <button
                type='button'
                onClick={() => onRemove(service)}
                aria-label={`Remove ${service.label || 'this service'}`}
                className='tw:text-muted-foreground tw:transition-colors tw:hover:text-destructive'
              >
                <Trash2 className='tw:size-4' />
              </button>
            </li>
          ))}
        </ul>
      )}

      {problem && <p className='tw:text-sm tw:text-destructive'>{problem}</p>}

      <div className='tw:flex tw:items-center tw:justify-between tw:gap-3'>
        <Button
          type='button'
          variant='outline'
          disabled={services.length >= experienceMaxOptionalServices}
          onClick={() => onAdd(makeOptionalService(nextOptionalServiceOrder(services)))}
        >
          <Plus className='tw:size-4' />
          Add a service
        </Button>
        {services.length === 0 ? (
          <span className='tw:text-sm tw:text-muted-foreground'>
            No extras offered. The booking form shows no such section.
          </span>
        ) : (
          <span className='tw:text-sm tw:text-muted-foreground'>
            {services.length === 1 ? '1 service' : `${services.length} services`}
            {' · '}
            up to {formatJpy(services.reduce((sum, service) => sum + service.price, 0))} if all are taken
          </span>
        )}
      </div>
    </div>
  );
}
